import { Dialog, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import * as Sentry from '@sentry/browser';
import { ApolloQueryResult } from 'apollo-client';
import { loader } from 'graphql.macro';
import React, { useState } from 'react';

import { memberClient } from 'src/config/Apollo';
import { Calendar, CalendarProfile } from 'src/models/Sessions';

import {
  CalendarModalBannerIcon,
  CalendarModalTestID,
  CalendarState,
  DeleteCalendarData,
} from './CalendarModal.models';
import { useStyles } from './CalendarModal.styles';
import { CalendarModalBanner } from './CalendarModalBanner';
import { CalendarModalDeleteConfirmation } from './CalendarModalDeleteConfirmation';
import { CalendarModalDeletedToZeroView } from './CalendarModalDeletedToZeroView';
import { CalendarModalGettingStartedView } from './CalendarModalGettingStartedView';
import { CalendarModalProfileView } from './CalendarModalProfileView';

export enum modalPhase {
  GetStarted,
  ViewProfiles,
  ConfirmDelete,
  DeletedToZero,
}

export interface DetermineShowBannerArgs {
  phase: modalPhase;
  missingCheckForConflicts?: boolean;
  newCalendarConnected?: boolean;
  calendarIsSyncing?: boolean;
  calendarWasSynced?: boolean;
  calendarSyncError?: boolean;
  calendarWasDeleted?: boolean;
  errorDeleting?: boolean;
  deleteProfileName?: string;
}

interface Props {
  profiles?: CalendarProfile[];
  newCalendarConnected: boolean;
  hasAvailableHours: boolean;
  missingCheckForConflicts: boolean;
  onDeleteCalendarProfile: () => Promise<ApolloQueryResult<any>>;
  handleSyncClick: () => Promise<ApolloQueryResult<any>>;
  onUpdateCalendarProfile: (uuid: string, state: CalendarState) => void;
  setNewCalendarConnect: (set: boolean) => void;
  onSendToAuthUrl: () => void;
  onContinue: () => void;
  onClose: () => void;
}

const calendarQuery = loader('../../../pages/documents/CalendarQuery.gql');
const updateCalendarMutation = loader('../documents/UpdateCalendar.gql');
const deleteCalendarMutation = loader('../documents/DeleteCalendar.gql');

export const determineShowBanner = ({
  phase,
  missingCheckForConflicts,
  newCalendarConnected,
  calendarIsSyncing,
  calendarWasSynced,
  calendarSyncError,
  calendarWasDeleted,
  errorDeleting,
  deleteProfileName,
}: DetermineShowBannerArgs) => {
  const triggerBanner = [
    missingCheckForConflicts,
    newCalendarConnected,
    calendarWasDeleted,
    errorDeleting,
    calendarIsSyncing,
    calendarWasSynced,
    calendarSyncError,
  ];
  let icon = CalendarModalBannerIcon.CheckCircle;
  let text = 'Your calendar was successfully connected!';

  if (phase !== modalPhase.ViewProfiles) {
    return { showBanner: false, icon, text };
  }

  switch (true) {
    case missingCheckForConflicts:
      icon = CalendarModalBannerIcon.ErrorX;
      text =
        'You must enable Check for Conflicts for at least one calendar for clients to book sessions.';
      break;
    case errorDeleting:
      icon = CalendarModalBannerIcon.ErrorX;
      text = 'There was an error deleting your calendar';
      break;
    case calendarWasDeleted:
      icon = CalendarModalBannerIcon.Trashcan;
      text = `Disconnected (${deleteProfileName})`;
      break;
    case calendarIsSyncing:
      icon = CalendarModalBannerIcon.SyncCircle;
      text = 'Resyncing your calendar...';
      break;
    case calendarWasSynced:
      text = 'Calendar resynced!';
      break;
    case calendarSyncError:
      text = 'There was a problem syncing your calendar';
      icon = CalendarModalBannerIcon.ErrorX;
      break;
    default:
      break;
  }

  return {
    showBanner: triggerBanner.find((val) => !!val),
    icon,
    text,
  };
};

export const CalendarModal: React.FC<Props> = ({
  newCalendarConnected,
  missingCheckForConflicts,
  hasAvailableHours,
  profiles = [],
  setNewCalendarConnect,
  onDeleteCalendarProfile,
  onUpdateCalendarProfile,
  handleSyncClick,
  onSendToAuthUrl,
  onClose,
  onContinue,
}) => {
  const [phase, setPhase] = useState(
    profiles.length > 0 ? modalPhase.ViewProfiles : modalPhase.GetStarted,
  );

  const [working, setWorking] = useState(false);
  const [calendarWasSynced, setCalendarWasSynced] = useState(false);
  const [calendarIsSyncing, setCalendarSyncing] = useState(false);
  const [calendarSyncError, setCalendarSyncError] = useState(false);
  const [deleteProfileId, setDeleteProfileId] = useState('');
  const [deleteProfileName, setDeleteProfileName] = useState('');
  const [deleteId, setDeleteId] = useState('');
  const [errorDeleting, setErrorDeleting] = useState(false);
  const [calendarWasDeleted, setCalendarWasDeleted] = useState(false);

  const showContinueButton = profiles.length > 0 && !hasAvailableHours;

  const isBannerError = missingCheckForConflicts || calendarWasDeleted || errorDeleting;
  const { showBanner, icon, text } = determineShowBanner({
    phase,
    missingCheckForConflicts,
    newCalendarConnected,
    calendarIsSyncing,
    calendarWasSynced,
    calendarSyncError,
    calendarWasDeleted,
    errorDeleting,
    deleteProfileName,
  });

  const classes = useStyles();

  const confirmDelete = (deleteData: DeleteCalendarData) => {
    const { uuid, profileName, profileId } = deleteData;

    setPhase(modalPhase.ConfirmDelete);
    setDeleteId(uuid);
    setDeleteProfileId(profileId);
    setDeleteProfileName(profileName);
  };

  const clearDeleteData = () => {
    setDeleteId('');
    setDeleteProfileId('');
    setDeleteProfileName('');
    setErrorDeleting(false);
    setCalendarWasDeleted(false);
  };

  const closeModal = () => {
    clearDeleteData();
    onClose();
  };

  const cancelDelete = () => {
    clearDeleteData();
    setNewCalendarConnect(false);
    setCalendarWasSynced(false);
    setPhase(modalPhase.ViewProfiles);
  };

  const doDelete = () => {
    setWorking(true);

    memberClient
      .mutate({
        mutation: deleteCalendarMutation,
        variables: { uuid: deleteId, profileId: deleteProfileId },
        refetchQueries: [{ query: calendarQuery }],
      })
      .then(async () => {
        setCalendarWasDeleted(true);
        await onDeleteCalendarProfile();
        setPhase(profiles.length === 1 ? modalPhase.DeletedToZero : modalPhase.ViewProfiles);
      })
      .catch(() => {
        setErrorDeleting(true);
        setPhase(modalPhase.ViewProfiles);
      })
      .finally(() => {
        setWorking(false);
      });
  };

  const syncCalendar = () => {
    setWorking(true);
    setCalendarSyncing(true);

    handleSyncClick()
      .then(() => {
        setCalendarWasSynced(true);
        setCalendarSyncError(false);
      })
      .catch(() => {
        setCalendarWasSynced(false);
        setCalendarSyncError(true);
      })
      .finally(() => {
        setCalendarSyncing(false);
        setWorking(false);
      });
  };

  const handleCalendarUpdate = async (calendar: Calendar, state: CalendarState) => {
    const { uuid } = calendar;
    const { isActive, isTarget } = state;

    setWorking(true);
    onUpdateCalendarProfile(uuid, state);

    try {
      await memberClient.mutate({
        mutation: updateCalendarMutation,
        variables: { uuid, isActive, isTarget },
      });
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      setWorking(false);
    }
  };

  const calendarModalBannerProps = { isError: isBannerError, icon, text };
  const getStartedProps = {
    missingCheckForConflicts,
    onClose: closeModal,
    working,
    onSendToAuthUrl,
  };
  const profileViewProps = {
    ...getStartedProps,
    profiles,
    confirmDelete,
    handleCalendarUpdate,
    handleSyncClick: syncCalendar,
    onContinue,
    showContinueButton,
    working,
  };

  return (
    <Dialog
      open
      onClose={closeModal}
      aria-labelledby="form-dialog-title"
      fullWidth={false}
      maxWidth="md"
      data-testid={CalendarModalTestID.Container}
    >
      <IconButton
        aria-label="Close"
        onClick={closeModal}
        className={classes.closeButton}
        data-testid={CalendarModalTestID.CloseButton}
      >
        <CloseIcon />
      </IconButton>

      {/* <ModalBannerComponent /> */}
      {showBanner && <CalendarModalBanner {...calendarModalBannerProps} />}

      {/* DialogView based on current modal phase */}
      {phase === modalPhase.GetStarted && <CalendarModalGettingStartedView {...getStartedProps} />}
      {phase === modalPhase.ViewProfiles && <CalendarModalProfileView {...profileViewProps} />}
      {phase === modalPhase.ConfirmDelete && (
        <CalendarModalDeleteConfirmation
          cancelDelete={cancelDelete}
          doDelete={doDelete}
          deleteProfileName={deleteProfileName}
          isLastCalendar={profiles.length === 1}
          working={working}
        />
      )}
      {phase === modalPhase.DeletedToZero && (
        <CalendarModalDeletedToZeroView onClose={closeModal} sendToAuthUrl={onSendToAuthUrl} />
      )}
    </Dialog>
  );
};
